import {
  CardSize,
  ChartId,
  DashboardDateConfig,
  DimensionId
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';

// Every dashboard card is now a reference to a saved bpm-types ChartConfig (graphic/table/stat).
// The sketch SPARK_CHART / DATA_TABLE card types are retired — spark = a `stat` ChartConfig with a
// sparkline, data tables = a `table` ChartConfig. All render through the shared ChartRenderer.
export const CardType = {
  CHART: 'chart'
} as const;
export type CardTypeValue = (typeof CardType)[keyof typeof CardType];

// Card sizes are PRESET NAMES owned by bpm-types (`standard` / `wide`) — the persisted vocabulary.
// Each name's columns and pixel height live in dashboard-constants.ts, so retuning a size, or
// moving off a 4-column grid, re-interprets every saved dashboard instead of invalidating it.

export interface CardPosition {
  x: number;
  y: number;
}

// A card's footprint in GRID CELLS: `width` columns by `height` rows (rows derived from the
// preset's pixel height — see SIZE_DIMENSIONS).
export interface CardDimensions {
  width: number;
  height: number;
}

// A chart card references a SAVED chart by id (never inlines the config). ChartCard fetches the
// real bpm-types ChartConfig + its data and renders via the shared ChartRenderer.
export interface ChartCardConfig {
  chartId: ChartId;
  // This card's own opt-outs from the dashboard filters (per placement, not per chart). Absent = none.
  ignoreDashboardFilters?: DimensionId[];
  // Stored opt-outs that failed validation, kept only to write back. Set `ignoreDashboardFilters`
  // only through withCardIgnoreDashboardFilters, which deletes this field.
  savedIgnoreDashboardFiltersRaw?: DimensionId[];
}

export type DashboardCardConfig = ChartCardConfig;

export interface DashboardCard {
  id: string;
  type: CardTypeValue;
  size: CardSize;
  position: CardPosition;
  sectionId?: string;
  config?: DashboardCardConfig;
}

// One dashboard change the agent asked for. A card change names a CHART, not a card: card ids are
// placement-derived and the agent never sees them. `slot` is 1-based in reading order.
export type LayoutChange =
  | { kind: 'resize'; chartId: ChartId; size: CardSize }
  | { kind: 'remove'; chartId: ChartId }
  | { kind: 'move'; chartId: ChartId; slot: number }
  | { kind: 'setCardFilterOptOut'; chartId: ChartId; ignore: DimensionId[] }
  | { kind: 'setDashboardFilters'; filters: FilterNode[] };

export interface DashboardSection {
  id: string;
  title: string;
  order: number;
}

export interface DashboardConfig {
  id: string;
  clientId: string;
  name: string;
  isDefault: boolean;
  cards: DashboardCard[];
  sections: DashboardSection[];
  // The date window this dashboard opens on, as the picker's own state. Absent on every dashboard
  // saved before this existed, and on any the user hasn't set one for — those fall back to the
  // trailing default. See date-config.ts.
  defaultDateConfig?: DashboardDateConfig;
  // The filters this dashboard opens with, applied to every chart card. Absent = none.
  defaultFilters?: FilterNode[];
  // A stored layout.defaultFilters that failed validation, kept only to write back. Set
  // `defaultFilters` only through withDefaultFilters, which deletes this field.
  savedDefaultFiltersRaw?: FilterNode[];
  // Who may see and who may change this dashboard. BOTH optional, and the absence is meaningful: an
  // UNSAVED dashboard has no database row and therefore no levels, which counts as editable. That
  // is what lets a user create the first dashboard at a company that has none.
  //
  // A dashboard is governed by its OWN levels, never by the charts on it. One internal chart on a
  // client dashboard must not take the whole dashboard away from that client — the chart draws a
  // locked card instead.
  viewRoleRequired?: number;
  editRoleRequired?: number;
}

// A Save of pending dashboard filters still awaiting its echo: the saved config it was sent from,
// and the filters it sent.
export interface SentFilterSave {
  from: DashboardConfig;
  filters: FilterNode[];
}

// The agent's pending dashboard filters (shown as the preview) and the Save they wait on.
export interface PendingFilterFlow {
  pending: FilterNode[] | undefined;
  sent: SentFilterSave | undefined;
  // Filters the view takes as its own. A new object each time, so each is applied once.
  viewUpdate: { filters: FilterNode[] } | undefined;
}

export type PendingFilterEvent =
  | {
      kind: 'agentSetFilters';
      filters: FilterNode[];
      config: DashboardConfig;
      viewFilters: FilterNode[];
    }
  | { kind: 'save'; config: DashboardConfig }
  | { kind: 'saveFailed' }
  | { kind: 'echo'; config: DashboardConfig }
  | { kind: 'discard' };

export interface CardTypeDefinition {
  id: CardTypeValue;
  name: string;
  defaultSize: CardSize;
  availableSizes: readonly CardSize[];
}
